import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ArrowPathIcon, PencilIcon, CheckIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { consolidateCompanyGroup, suggestCompanyInfo } from '../../../api/quarantine.api';
import { useNotification } from '../../../context/NotificationContext';

// --- Types ---

interface VariantCityDto {
  name: string;
  stateName?: string;
  candidateCount: number;
}

interface VariantUrlDto {
  url: string;
  candidateCount: number;
}

interface CompanyVariantDto {
  companyIds: string[];
  name: string;
  urlCount: number;
  locations: VariantCityDto[];
  urls: VariantUrlDto[];
  linkedIn?: string;
  isConfirmed: boolean;
  candidateCount: number;
  isAiConfirmedMatch: boolean;
  isSubsidiary: boolean;
  subsidiaryNote?: string;
}

export interface SimilarCompanyGroup {
  suggestedCorrectName: string;
  variants: CompanyVariantDto[];
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: SimilarCompanyGroup[];
  totalGroups: number;
  isLoading: boolean;
  error: string | null;
  onRemoveGroup: (suggestedName: string) => void;
  token: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRecompute: () => void;
}

function SimilarCompanyGroupsModal({ isOpen, onClose, groups, totalGroups, isLoading, error, onRemoveGroup, token, currentPage, onPageChange, onRecompute }: ModalProps) {
  const [isFixing, setIsFixing] = useState<string | null>(null);
  const [isFixingAll, setIsFixingAll] = useState(false);
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);

  const [editedNames, setEditedNames] = useState<Map<string, string>>(new Map());
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null);

  const [editedUrls, setEditedUrls] = useState<Map<string, string>>(new Map());
  const [editedLinkedIns, setEditedLinkedIns] = useState<Map<string, string>>(new Map());
  const [isSuggesting, setIsSuggesting] = useState<Set<string>>(new Set());
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);

  const { error: notifyError, success: notifySuccess } = useNotification();

  // Reset state when page changes to ensure we fetch new suggestions for new groups
  useEffect(() => {
    setEditedNames(new Map());
    setEditedUrls(new Map());
    setEditedLinkedIns(new Map());
    setIsSuggesting(new Set());
    setDismissedKeys(new Set());
  }, [currentPage]);

  const getEffectiveName = useCallback((group: SimilarCompanyGroup) =>
    editedNames.get(group.suggestedCorrectName) ?? group.suggestedCorrectName, [editedNames]);
  const getEffectiveUrl = useCallback((group: SimilarCompanyGroup) => editedUrls.get(group.suggestedCorrectName) ?? '', [editedUrls]);
  const getEffectiveLinkedIn = useCallback((group: SimilarCompanyGroup) => editedLinkedIns.get(group.suggestedCorrectName) ?? '', [editedLinkedIns]);

  const getVariantKey = useCallback((variant: CompanyVariantDto) => variant.companyIds.join(','), []);

  const getActiveVariants = useCallback((group: SimilarCompanyGroup) =>
    group.variants.filter(v => !dismissedKeys.has(getVariantKey(v))), [dismissedKeys, getVariantKey]);

  const getDismissedVariants = useCallback((group: SimilarCompanyGroup) =>
    group.variants.filter(v => dismissedKeys.has(getVariantKey(v))), [dismissedKeys, getVariantKey]);

  const handleDismissVariant = (variant: CompanyVariantDto) => {
    const key = getVariantKey(variant);
    setDismissedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSuggestInfo = useCallback(async (group: SimilarCompanyGroup) => {
    const key = group.suggestedCorrectName;
    if (isSuggesting.has(key)) return;
    setIsSuggesting(prev => new Set(prev).add(key));
    try {
      const activeVariants = getActiveVariants(group);
      const names = activeVariants.map(v => v.name);
      const urls = activeVariants.map(v => v.urls.map(u => u.url));
      const linkedIns = activeVariants.map(v => v.linkedIn ? [v.linkedIn] : []);
      const locations = activeVariants.map(v => v.locations.map(l => l.name));
      const result = await suggestCompanyInfo(names, urls, linkedIns, locations, token);
      setEditedUrls(prev => { const next = new Map(prev); next.set(key, result.websiteUrl); return next; });
      setEditedLinkedIns(prev => { const next = new Map(prev); next.set(key, result.linkedInUrl); return next; });
    } catch (err: any) {
      notifyError("AI Suggestion Failed", err.message);
    } finally {
      setIsSuggesting(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [token, getActiveVariants, isSuggesting, notifyError]);

  // Automatically trigger AI suggestions for all groups on the current page
  useEffect(() => {
    if (isOpen && groups.length > 0 && !isLoading && !isFixingAll) {
      groups.forEach((group) => {
        const key = group.suggestedCorrectName;
        const hasEdited = editedUrls.has(key) || editedLinkedIns.has(key);
        const suggesting = isSuggesting.has(key);
        if (!hasEdited && !suggesting) {
          handleSuggestInfo(group);
        }
      });
    }
  }, [isOpen, groups, isLoading, isFixingAll, handleSuggestInfo, editedUrls, editedLinkedIns, isSuggesting]);

  const executeConsolidateGroup = async (group: SimilarCompanyGroup) => {
    const variantsToFix = getActiveVariants(group);
    const name = getEffectiveName(group);
    const url = getEffectiveUrl(group);
    const linkedIn = getEffectiveLinkedIn(group);
    setConfirmAction(null);
    setIsFixing(group.suggestedCorrectName);
    try {
      await consolidateCompanyGroup({
        correctName: name,
        correctUrl: url,
        correctLinkedIn: linkedIn || undefined,
        companyIds: variantsToFix.flatMap(v => v.companyIds),
      }, token);
      const totalCandidates = variantsToFix.reduce((s, v) => s + v.candidateCount, 0);
      notifySuccess("Group Consolidated", `Created "${name}", rejected ${variantsToFix.length} variant(s), remapped ${totalCandidates} candidate(s).`);
      onRemoveGroup(group.suggestedCorrectName);
    } catch (err: any) {
      notifyError("Consolidation Failed", err.message);
    } finally {
      setIsFixing(null);
    }
  };

  const handleConsolidateGroup = (group: SimilarCompanyGroup) => {
    const variantsToFix = getActiveVariants(group);
    if (variantsToFix.length === 0) return;
    const name = getEffectiveName(group);
    const url = getEffectiveUrl(group);
    const totalCandidates = variantsToFix.reduce((s, v) => s + v.candidateCount, 0);
    setConfirmAction({
      message: `This will create a new entry "${name}" (URL: ${url || 'not set'}), reject ${variantsToFix.length} variant(s), and remap ${totalCandidates} candidate(s) to the new entry. Continue?`,
      onConfirm: () => executeConsolidateGroup(group),
    });
  };

  const fixableGroupCount = groups.filter(g => getActiveVariants(g).length > 0).length;

  const executeConsolidateAll = useCallback(async (isAuto = false) => {
    setConfirmAction(null);
    setIsFixingAll(true);
    try {
      let successCount = 0;
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const variants = getActiveVariants(group);
        if (variants.length === 0) continue;
        
        const key = group.suggestedCorrectName;
        const name = editedNames.get(key) ?? group.suggestedCorrectName;
        const url = editedUrls.get(key) ?? '';
        const linkedIn = editedLinkedIns.get(key) ?? '';

        await consolidateCompanyGroup({
          correctName: name,
          correctUrl: url,
          correctLinkedIn: linkedIn || undefined,
          companyIds: variants.flatMap(v => v.companyIds),
        }, token);
        successCount++;
      }
      notifySuccess("Page Consolidated", `Consolidated ${successCount} group(s) successfully.`);
      
      if (isAuto && currentPage >= Math.ceil(totalGroups / 10)) {
        setIsAutoProcessing(false);
        notifySuccess("Automation Complete", "All pages have been processed.");
      }
      
      onPageChange(currentPage + 1);
    } catch (err: any) {
      notifyError("Consolidation Failed", err.message);
      setIsAutoProcessing(false);
    } finally {
      setIsFixingAll(false);
    }
  }, [groups, getActiveVariants, editedNames, editedUrls, editedLinkedIns, token, notifySuccess, currentPage, totalGroups, onPageChange, notifyError]);

  // Auto-Process Loop
  useEffect(() => {
    if (isAutoProcessing && isOpen && groups.length > 0 && !isLoading && !isFixingAll) {
      // Check if all groups have suggestions (or are in progress)
      const allReady = groups.every((group) => {
        const variants = getActiveVariants(group);
        if (variants.length === 0) return true;
        const key = group.suggestedCorrectName;
        return editedUrls.has(key) && editedLinkedIns.has(key);
      });
      
      const noneSuggesting = isSuggesting.size === 0;

      if (allReady && noneSuggesting) {
        // Short delay to let the user see what's happening
        const timer = setTimeout(() => {
          executeConsolidateAll(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isAutoProcessing, isOpen, groups, isLoading, isFixingAll, editedUrls, editedLinkedIns, isSuggesting, fixableGroupCount, executeConsolidateAll, getActiveVariants]);

  const handleConsolidateAll = () => {
    if (fixableGroupCount === 0) return;
    setConfirmAction({
      message: `Fix all ${fixableGroupCount} group(s) on this page? A new company entry will be created for each group, old variants will be rejected, and all candidates remapped.`,
      onConfirm: executeConsolidateAll,
    });
  };

  return (
    <>
      <Dialog open={isOpen} onClose={() => { if (!confirmAction && !isFixing && !isFixingAll) onClose(); }} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-5xl overflow-hidden rounded-lg bg-white p-6 shadow-xl dark:bg-darkbglight">
            <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white flex justify-between items-center">
              Similar Company Groups ({isLoading ? '...' : totalGroups} Found)
              <div className="flex items-center gap-4">
                <button
                  onClick={onRecompute}
                  disabled={isLoading || isFixingAll || isAutoProcessing}
                  title="Recompute groups"
                  className="rounded-md border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
                {/* <button
                  onClick={() => setIsAutoProcessing(!isAutoProcessing)}
                  disabled={isLoading || isFixingAll || fixableGroupCount === 0}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm flex items-center gap-2 ${
                    isAutoProcessing 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  <SparklesIcon className="h-4 w-4" />
                  {isAutoProcessing ? 'Stop Auto-Process' : 'Auto-Process All Pages'}
                </button> */}
                <button
                  onClick={handleConsolidateAll}
                  disabled={isLoading || isFixingAll || isAutoProcessing || fixableGroupCount === 0}
                  className="rounded-md bg-entntblue px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-entntorange disabled:bg-gray-400"
                >
                  {isFixingAll ? 'Fixing...' : `Fix Page ${currentPage} (${fixableGroupCount} Groups)`}
                </button>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </Dialog.Title>

            <div className="mt-4 max-h-[75vh] overflow-y-auto pr-4 -mr-4">
              {isLoading ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-10">Loading groups... This may take a moment.</p>
              ) : error ? (
                <p className="text-center text-red-500 py-10">Error: {error}</p>
              ) : groups.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-10">No similar company groups found.</p>
              ) : (
                <div className="space-y-6">
                  {groups.map((group, index) => {
                    const variantsToFix = getActiveVariants(group);
                    const dismissedVariants = getDismissedVariants(group);
                    const suggesting = isSuggesting.has(group.suggestedCorrectName);
                    return (
                      <div
                        key={index}
                        className="rounded-md border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-darkbg"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-200">
                            Group {index + 1}: <span className="text-entntblue">{group.suggestedCorrectName}</span>
                            <span className="text-sm font-normal text-gray-500 ml-2">
                              ({variantsToFix.length} variant{variantsToFix.length !== 1 ? 's' : ''}, {group.variants.reduce((sum, v) => sum + v.candidateCount, 0)} candidates)
                            </span>
                          </h3>
                          <button
                            onClick={() => handleConsolidateGroup(group)}
                            disabled={isFixingAll || isFixing === group.suggestedCorrectName || variantsToFix.length === 0}
                            className="rounded bg-entntblue/10 px-2.5 py-1 text-xs font-semibold text-entntblue shadow-sm hover:bg-entntblue/20 disabled:bg-gray-200 disabled:text-gray-500"
                          >
                            {isFixing === group.suggestedCorrectName ? 'Fixing...' : `Fix ${variantsToFix.length} Variant(s)`}
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* New entry fields */}
                          <div className="space-y-3">
                            {/* Correct Name */}
                            <div>
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">New Company Name</p>
                              <div className="mt-1 rounded-md border border-green-200 bg-green-100 p-3 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200 flex items-center gap-2">
                                {editingGroupIndex === index ? (
                                  <>
                                    <input
                                      type="text"
                                      autoFocus
                                      value={getEffectiveName(group)}
                                      onChange={(e) => setEditedNames(prev => { const next = new Map(prev); next.set(group.suggestedCorrectName, e.target.value); return next; })}
                                      onKeyDown={(e) => { if (e.key === 'Enter') setEditingGroupIndex(null); }}
                                      className="flex-1 bg-white dark:bg-darkbg border border-green-300 rounded px-2 py-1 text-sm font-bold text-green-900 dark:text-green-200 focus:outline-none focus:ring-1 focus:ring-green-400"
                                    />
                                    <button onClick={() => setEditingGroupIndex(null)} className="p-1 rounded hover:bg-green-200 dark:hover:bg-green-800">
                                      <CheckIcon className="h-4 w-4 text-green-700 dark:text-green-300" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <p className="font-bold flex-1">{getEffectiveName(group)}</p>
                                    <button
                                      onClick={() => {
                                        if (!editedNames.has(group.suggestedCorrectName)) setEditedNames(prev => { const next = new Map(prev); next.set(group.suggestedCorrectName, group.suggestedCorrectName); return next; });
                                        setEditingGroupIndex(index);
                                      }}
                                      className="p-1 rounded hover:bg-green-200 dark:hover:bg-green-800"
                                    >
                                      <PencilIcon className="h-3.5 w-3.5 text-green-700 dark:text-green-300" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* URL + LinkedIn with Suggest button */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Website URL &amp; LinkedIn</p>
                                {/* <button
                                  onClick={() => handleSuggestInfo(group)}
                                  disabled={isSuggesting.has(group.suggestedCorrectName) || isFixingAll}
                                  className="flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 dark:bg-purple-900/30 dark:text-purple-300"
                                >
                                  <SparklesIcon className="h-3 w-3" />
                                  {isSuggesting.has(group.suggestedCorrectName) ? 'Suggesting...' : 'AI Suggest'}
                                </button> */}
                              </div>
                              <div className="space-y-1.5">
                                <input
                                  type="text"
                                  placeholder="e.g. infosys.com"
                                  value={getEffectiveUrl(group)}
                                  onChange={(e) => setEditedUrls(prev => { const next = new Map(prev); next.set(group.suggestedCorrectName, e.target.value); return next; })}
                                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-entntblue"
                                />
                                <input
                                  type="text"
                                  placeholder="e.g. linkedin.com/company/infosys"
                                  value={getEffectiveLinkedIn(group)}
                                  onChange={(e) => setEditedLinkedIns(prev => { const next = new Map(prev); next.set(group.suggestedCorrectName, e.target.value); return next; })}
                                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-entntblue"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Variants */}
                          <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Variants to Reject ({variantsToFix.length})</p>
                            <div className="space-y-2 mt-1">
                              {variantsToFix.map(variant => {
                                const isAiFlagged = !variant.isAiConfirmedMatch;
                                const aiLabel = isAiFlagged
                                  ? (variant.isSubsidiary ? 'AI: Likely a subsidiary' : 'AI: Likely unrelated company')
                                  : null;

                                return (
                                  <div
                                    key={getVariantKey(variant)}
                                    className={`rounded-md border p-3 overflow-hidden relative ${
                                      isAiFlagged
                                        ? 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-200'
                                        : 'border-blue-200 bg-blue-100 text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                                    }`}
                                  >
                                    <button
                                      onClick={() => handleDismissVariant(variant)}
                                      title="Exclude from this group"
                                      className={`absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold leading-none ${
                                        isAiFlagged
                                          ? 'bg-yellow-300 text-yellow-800 hover:bg-yellow-400'
                                          : 'bg-blue-300 text-blue-800 hover:bg-blue-400'
                                      }`}
                                    >
                                      −
                                    </button>
                                    <p className={`font-bold pr-6 ${isAiFlagged ? 'line-through' : ''}`}>{variant.name}</p>
                                    {variant.locations && variant.locations.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {variant.locations.map((loc, i) => (
                                          <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${
                                            isAiFlagged
                                              ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                              : 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                                          }`}>
                                            {loc.name} | {loc.candidateCount}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-xs mt-1">{variant.urlCount} URL{variant.urlCount !== 1 ? 's' : ''} · {variant.candidateCount} candidate(s)</p>
                                    {variant.urls && variant.urls.length > 0 && (
                                      <div className="mt-1.5 space-y-0.5">
                                        {variant.urls.map((u, i) => {
                                          const href = u.url.startsWith('http://') || u.url.startsWith('https://') ? u.url : `https://${u.url}`;
                                          return (
                                            <p key={i} className={`text-xs truncate max-w-[400px] ${
                                              isAiFlagged ? 'text-yellow-700 dark:text-yellow-400' : 'text-blue-700 dark:text-blue-300'
                                            }`} title={u.url}>
                                              <a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline">{u.url}</a>
                                              {' '}— {u.candidateCount}
                                            </p>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {isAiFlagged && (
                                      <p className="text-xs mt-1 font-semibold">
                                        {aiLabel}{variant.subsidiaryNote ? ` — ${variant.subsidiaryNote}` : ''}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                              {dismissedVariants.map(variant => (
                                <div key={getVariantKey(variant)} className="rounded-md border border-gray-200 bg-gray-100 p-3 text-gray-400 dark:border-gray-700 dark:bg-gray-800/30 dark:text-gray-500 overflow-hidden relative opacity-50">
                                  <button
                                    onClick={() => handleDismissVariant(variant)}
                                    title="Re-include in this group"
                                    className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-300 text-gray-600 hover:bg-gray-400 text-xs font-bold leading-none"
                                  >
                                    +
                                  </button>
                                  <p className="font-bold pr-6 line-through">{variant.name}</p>
                                  {variant.locations && variant.locations.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {variant.locations.map((loc, i) => (
                                        <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">
                                          {loc.name} | {loc.candidateCount}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <p className="text-xs mt-1">{variant.urlCount} URL{variant.urlCount !== 1 ? 's' : ''} · {variant.candidateCount} candidate(s)</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {!isLoading && !error && totalGroups > 0 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {(currentPage - 1) * 10 + 1}–{Math.min(currentPage * 10, totalGroups)} of {totalGroups} groups
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || isLoading || isFixingAll}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {Math.ceil(totalGroups / 10)}
                  </span>
                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= Math.ceil(totalGroups / 10) || isLoading || isFixingAll}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <Dialog open={true} onClose={() => setConfirmAction(null)} className="relative z-[60]">
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-darkbglight">
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Consolidation
              </Dialog.Title>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {confirmAction.message}
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmAction.onConfirm()}
                  className="rounded-md bg-entntblue px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-entntorange"
                >
                  Confirm
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default SimilarCompanyGroupsModal;
